import React, { useContext, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AccountContext } from '../../Accounts';
import { DataTypeContext } from '../../DataTypes';
import { API, PROVISION_DISPLAY_TYPE } from '../../Constants';
import { formatDate, formatTime, mapValues } from '../../Utils';
import CheckboxField from '../../form/CheckboxField';
import TextField from '../../form/TextField';
import { useCustomTranslator } from '../../utils/customTranslator';

const ID_PREFIX = 'id';
const CONFIRMATION_DATE_FORMAT = 'dddd, MMMM D, YYYY [at] h:mm A';

const ConfirmBooking = () => {
    const { t } = useTranslation();
    const customT = useCustomTranslator();
    const history = useHistory();
    const location = useLocation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const {
        clientId,
        selectedAppointmentSlot,
        selectedAppointmentLocation,
        setIsDeliveryAppointment,
    } = useContext(AccountContext);
    const { dataTypes, useProvisionsAndNotes } = useContext(DataTypeContext);
    const appointmentCutOffHours = dataTypes?.appointmentOptions?.appointmentCutOffHours || 48;
    const [isBooking, setBooking] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const routeState = location?.state || {};
    const provisions = routeState.provisions || [];
    const programIdFromState = routeState.program;
    const selectedProgramNameFromState = routeState.selectedProgramName;
    const isDelivery = Boolean(routeState.isDelivery);
    const existingAppointment = routeState.existingAppointment;
    const activityId = existingAppointment?.id;
    const selectedAgency = isDelivery ? selectedAppointmentSlot?.locationId : routeState.agency;
    const returnToSchedulePath = isDelivery ? '/book-delivery' : '/book-appointment';

    const selectedProgramId = selectedAppointmentSlot?.programId || programIdFromState;

    const selectedProgramName = useMemo(() => {
        if (selectedProgramNameFromState) {
            return selectedProgramNameFromState;
        }

        if (selectedAppointmentSlot?.programName) {
            return selectedAppointmentSlot.programName;
        }

        const programs = selectedAppointmentLocation?.programs || [];
        return programs.find((program) => `${program.id}` === `${selectedProgramId}`)?.name || '';
    }, [selectedAppointmentSlot, selectedAppointmentLocation, selectedProgramId, selectedProgramNameFromState]);

    const appointmentDateTime = useMemo(() => {
        if (selectedAppointmentSlot?.availableDateTime) {
            const dateTime = moment(selectedAppointmentSlot.availableDateTime);
            if (dateTime.isValid()) {
                return dateTime.format(CONFIRMATION_DATE_FORMAT);
            }
        }

        if (selectedAppointmentSlot?.dateObj && selectedAppointmentSlot?.time24) {
            const dateTime = moment(`${formatDate(selectedAppointmentSlot.dateObj)} ${selectedAppointmentSlot.time24}`, 'MM-DD-YYYY HH:mm');
            if (dateTime.isValid()) {
                return dateTime.format(CONFIRMATION_DATE_FORMAT);
            }
        }

        return '';
    }, [selectedAppointmentSlot]);

    const schema = Yup.object({
        provisions: Yup.lazy((obj) =>
            Yup.object(
                mapValues(obj || {}, (value) => {
                    if (typeof value !== 'boolean') {
                        return Yup.number().min(0, 'The value must be positive');
                    }
                })
            )
        ),
    });

    const handleConfirm = async (values) => {
        if (!selectedAppointmentSlot || !selectedAgency) {
            history.push('/book-appointment');
            return;
        }

        try {
            setBooking(true);
            setErrorMessage('');
            const token = await executeRecaptcha('appointments');
            const payload = { token };

            if (isDelivery) {
                payload.date = formatDate(selectedAppointmentSlot.dateObj);
                payload.time = formatTime(selectedAppointmentSlot.time24);
            } else {
                payload.date = selectedAppointmentSlot.availableDate;
                payload.time = selectedAppointmentSlot.availableTime;
            }

            if (!existingAppointment) {
                const findResult = await (await fetch(`${API.FIND_CLIENT_URL}/${clientId}`)).json();
                payload.clientProfileId = findResult.clientProfileId;
            }

            const createURL = `${API.CREATE_APPOINTMENTS_URL}/${selectedAgency}`;
            const updateURL = `${API.UPDATE_APPOINTMENTS_URL}/${selectedAgency}/${activityId}`;
            const url = existingAppointment ? updateURL : createURL;

            if (selectedProgramId) {
                payload.programId = selectedProgramId;
            }

            if (useProvisionsAndNotes) {
                if (values?.includeNotes && values?.notes) {
                    payload.notes = values.notes;
                }

                if (values?.includeRequests && values?.provisions) {
                    payload.provisions = {};
                    for (const [key, value] of Object.entries(values.provisions)) {
                        const id = key.substring(ID_PREFIX.length);
                        if (typeof value === 'boolean' && value) {
                            payload.provisions[id] = 1;
                        } else if (value !== '' && value !== null && value !== undefined) {
                            payload.provisions[id] = parseFloat(value);
                        }
                    }
                }
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await response.json();

            if (response.ok) {
                setIsDeliveryAppointment(isDelivery);
                history.push('/booking-confirmed', { selectedProgramName });
                return;
            }

            if (data?.code) {
                const customError = customT(`api.error.${data.code}`) || data?.message || data?.error;
                setErrorMessage(customError);
            } else {
                setErrorMessage(customT('api.error.UNKNOWN'));
            }
            setBooking(false);
        } catch (error) {
            setErrorMessage(customT('api.error.UNKNOWN'));
            setBooking(false);
        }
    };

    const checkboxProvisions = [];
    const fieldProvisions = [];
    provisions.forEach((provision) => {
        if (provision.provisionDisplay === PROVISION_DISPLAY_TYPE.CHECKBOX) {
            checkboxProvisions.push(provision);
        } else {
            fieldProvisions.push(provision);
        }
    });

    if (!selectedAppointmentSlot) {
        return (
            <Row className="justify-content-center my-4 my-md-5">
                <Col md={6}>
                    <div className="card shadow-sm rounded p-4 text-center">
                        <p className="mb-3">{t('appointment.confirmationMissingContext')}</p>
                        <Link to="/book-appointment">{t('dashboard.bookServices')}</Link>
                    </div>
                </Col>
            </Row>
        );
    }

    return (
        <Formik
            validationSchema={schema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleConfirm}
            initialValues={{ includeRequests: false, includeNotes: false, notes: '', provisions: {} }}
        >
            {({ handleSubmit, values, errors, setFieldValue }) => (
                <Col md={6} sm={12} className="mx-auto">
                    <Form noValidate onSubmit={handleSubmit} className="card shadow-sm rounded p-4">
                        <h4 className="mb-3 text-center">{t('appointment.confirmAppointmentHeading')}</h4>
                        <p className="text-muted text-center mb-4">{t('appointment.confirmAppointmentSubheading')}</p>

                        <div className="border rounded p-4 mb-4">
                            <h5 className="mb-3">{t('appointment.appointmentDetailsHeading')}</h5>

                            <p className="text-muted mb-1">{t('appointment.dateAndTime')}</p>
                            <p className="fw-semibold mb-3">{appointmentDateTime}</p>

                            <p className="text-muted mb-1">{t('appointment.agency')}</p>
                            <p className="fw-semibold mb-3">{selectedAppointmentLocation?.nameWithAddress || ''}</p>

                            {selectedProgramName && (
                                <>
                                    <p className="text-muted mb-1">{t('appointment.program')}</p>
                                    <p className="fw-semibold mb-0">{selectedProgramName}</p>
                                </>
                            )}
                        </div>

                        {useProvisionsAndNotes && (
                            <>
                                {checkboxProvisions.length > 0 || fieldProvisions.length > 0 ? (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                id="includeRequests"
                                                checked={Boolean(values.includeRequests)}
                                                onChange={(event) => {
                                                    const shouldShow = event.target.checked;
                                                    setFieldValue('includeRequests', shouldShow);
                                                    if (!shouldShow) {
                                                        setFieldValue('provisions', {});
                                                    }
                                                }}
                                                label={
                                                    <span>
                                                        <span className="font-weight-bold text-dark">{t('appointment.requestItemsLabel')}</span>{' '}
                                                        <span className="text-muted small">({t('appointment.requestItemsHint')})</span>
                                                    </span>
                                                }
                                            />
                                        </Form.Group>

                                        {Boolean(values.includeRequests) && (
                                            <Form.Group className="mb-4">
                                                {checkboxProvisions.length > 0 && (
                                                    <Row wrap="true" className="mb-3">
                                                        {checkboxProvisions.map((provision) => {
                                                            const fieldName = `provisions.${ID_PREFIX}${provision.provisionId}`;
                                                            return (
                                                                <Col key={provision.provisionId} md={6} className="mb-2">
                                                                    <CheckboxField
                                                                        id={fieldName}
                                                                        name={fieldName}
                                                                        label={provision.provisionName}
                                                                        value={Boolean(values?.provisions?.[`${ID_PREFIX}${provision.provisionId}`])}
                                                                    />
                                                                </Col>
                                                            );
                                                        })}
                                                    </Row>
                                                )}

                                                {fieldProvisions.length > 0 && (
                                                    <Row wrap="true">
                                                        {fieldProvisions.map((provision) => {
                                                            const fieldName = `provisions.${ID_PREFIX}${provision.provisionId}`;
                                                            const errorProvision = errors.provisions
                                                                ? errors.provisions[`${ID_PREFIX}${provision.provisionId}`]
                                                                : null;

                                                            return (
                                                                <React.Fragment key={provision.provisionId}>
                                                                    <Col xs={3} md={2} className="mb-2">
                                                                        <Form.Group>
                                                                            <TextField
                                                                                id={fieldName}
                                                                                name={fieldName}
                                                                                value={values.provisions?.[`${ID_PREFIX}${provision.provisionId}`] || ''}
                                                                                placeholder="0"
                                                                                type="number"
                                                                                min={0}
                                                                                isInvalid={Boolean(errorProvision)}
                                                                            />
                                                                            <Form.Control.Feedback type="invalid">{errorProvision}</Form.Control.Feedback>
                                                                        </Form.Group>
                                                                    </Col>
                                                                    <Col xs={9} md={10} className="mb-2 d-flex align-items-center">
                                                                        {provision.provisionName}
                                                                    </Col>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </Row>
                                                )}
                                            </Form.Group>
                                        )}
                                    </>
                                ) : null}

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        id="includeNotes"
                                        checked={Boolean(values.includeNotes)}
                                        onChange={(event) => {
                                            const shouldShow = event.target.checked;
                                            setFieldValue('includeNotes', shouldShow);
                                            if (!shouldShow) {
                                                setFieldValue('notes', '');
                                            }
                                        }}
                                        label={
                                            <span>
                                                <span className="font-weight-bold text-dark">{t('appointment.addNotesLabel')}</span>{' '}
                                                <span className="text-muted small">({t('appointment.addNotesHint')})</span>
                                            </span>
                                        }
                                    />
                                </Form.Group>

                                {Boolean(values.includeNotes) && (
                                    <Form.Group className="mb-4">
                                        <TextField
                                            as="textarea"
                                            rows={4}
                                            maxLength={250}
                                            name="notes"
                                            placeholder={t('appointment.addNotesPlaceholder')}
                                        />
                                    </Form.Group>
                                )}
                            </>
                        )}

                        <div className="border rounded p-3 text-muted mb-4">{t('appointment.confirmationDisclaimer', { hours: appointmentCutOffHours })}</div>

                        {errorMessage && (
                            <p className="text-center text-danger mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                                {errorMessage}
                            </p>
                        )}

                        <div className="appointment-confirm-actions">
                            <div className="mb-2">
                                <Button variant="primary" className="w-100 py-2" type="submit" disabled={isBooking}>
                                    {isBooking ? t('appointment.booking') : t('appointment.confirmAppointmentButton')}
                                </Button>
                            </div>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    className="w-100 py-2"
                                    onClick={() => history.push(returnToSchedulePath, { existingAppointment })}
                                    disabled={isBooking}
                                >
                                    {t('appointment.backToScheduleButton')}
                                </Button>
                            </div>
                        </div>
                    </Form>

                    <p className="text-center mt-3 mb-0">
                        <Link to="/dashboard">{t('dashboard.return')}</Link>
                    </p>
                </Col>
            )}
        </Formik>
    );
};

export default ConfirmBooking;